import type {Context, NodePatchResult} from '../types';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  Expressions,
  Types,
  type Type,
  type ExpressionPath,
  Plugins,
  Variable,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  EBoolean,
  Expression,
  ECall,
  EList,
  ERecord,
  EVariable,
  EUnion,
} from '@packages/systems/wfdl-ast';
import {constant, identity, constantFalse} from '@packages/utilities/fp/utils';
import {buildNewCondition} from '@packages/systems/plugins-utils/Conditions';

const VISIBILITY_CONDITIONS_PATH: ExpressionPath = [
  {in: 'Record', at: 'visibility'},
  {in: 'Record', at: 'conditions'},
];

export default function patchLegacyVisibilityConditionsToConditionals(
  context: Context
): NodePatchResult {
  const {jsonNode} = context.node;
  const {dataTypes} = context.extraContext;

  if (jsonNode.type === 'Symbol') {
    return patchComponentInstance(jsonNode, dataTypes);
  }

  return patchElement(jsonNode);
}

/**
 * Migrate visibility.conditions setting value with legacy conditions to
 * the equivalent conditional in node.bind.
 */
function patchElement(jsonNode: JSONNode): NodePatchResult {
  let migrateVisibilityConditionsInBind = false;

  if (jsonNode.bind) {
    const visibilityConditionsExpr = Expressions.getIn(
      jsonNode.bind,
      VISIBILITY_CONDITIONS_PATH
    );
    migrateVisibilityConditionsInBind = Boolean(
      visibilityConditionsExpr &&
      !Expressions.isBinding(visibilityConditionsExpr)
    );
  }

  if (migrateVisibilityConditionsInBind) {
    return {
      needed: true,
      logData: {
        msg: 'Legacy Visibility Setting Conditions Migration: Migrated element visibility setting',
        meta: {
          nodeId: jsonNode._id,
        },
      },
      apply: () => {
        migrateBindAtPath(
          jsonNode,
          VISIBILITY_CONDITIONS_PATH,
          migrateVisibilityConditionsExpression
        );
      },
    };
  }

  return {
    needed: false,
    logData: {
      msg: 'Legacy Visibility Setting Conditions Migration: Skipping element',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {},
  };
}

const SYM_OVERRIDES_PATH: ExpressionPath = [
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
];

/**
 * Migrate visibility prop overrides with legacy conditions to the equivalent
 * conditional in node.bind.
 */
function patchComponentInstance(
  jsonNode: JSONNode,
  dataTypes: Plugins.DataTypes
): NodePatchResult {
  const propIdsToMigrate: string[] = [];

  if (jsonNode.bind) {
    let dataType: Type | undefined;
    // if component instance has instDataType, we rely on that as the source of truth
    if (jsonNode.data && jsonNode.data.sym) {
      if (jsonNode.data.sym.instDataType) {
        if (typeof jsonNode.data.sym.instDataType !== 'string') {
          dataType = jsonNode.data.sym.instDataType;
        } else if (
          // avoid parsing the stringified Type object if we can
          jsonNode.data.sym.instDataType.indexOf(
            '["Visibility","VisibilityConditions"]'
          ) > -1
        ) {
          dataType = JSON.parse(jsonNode.data.sym.instDataType);
        }
        if (dataType) {
          dataType =
            Types.__DEPRECATED__getTypeAtExpressionPath(
              dataType,
              SYM_OVERRIDES_PATH
            ) || undefined;
        }
      } else if (typeof jsonNode.data.sym.inst === 'string') {
        // if there is no instDataType, use type from ExtraContext.dataTypes
        dataType = dataTypes.get(['__SitePlugin', jsonNode.data.sym.inst]);
      }
    }

    // find VisibilityConditions prop overrides from node.bind
    // if the override is not a binding, then it is highly likely a
    // list of legacy visibility conditions
    if (dataType) {
      const fields = Types.getRecordFields(dataType);
      if (fields) {
        for (const propId of Object.keys(fields)) {
          const propType = fields[propId];
          if (propType && Types.isVisibilityConditionsType(propType)) {
            const propExpr = Expressions.getIn(jsonNode.bind, [
              {in: 'Record', at: 'sym'},
              {in: 'Record', at: 'overrides'},
              {in: 'Record', at: propId},
            ]);

            if (propExpr && !Expressions.isBinding(propExpr)) {
              propIdsToMigrate.push(propId);
            }
          }
        }
      }
    }
  }

  if (propIdsToMigrate.length > 0) {
    return {
      needed: true,
      logData: {
        msg: 'Legacy Visibility Setting Conditions Migration: Migrated component instance overrides',
        meta: {
          nodeId: jsonNode._id,
        },
      },
      apply: () => {
        for (const propId of propIdsToMigrate) {
          migrateBindAtPath(
            jsonNode,
            [
              {in: 'Record', at: 'sym'},
              {in: 'Record', at: 'overrides'},
              {in: 'Record', at: propId},
            ],
            migrateVisibilityConditionsExpression
          );
        }
      },
    };
  }

  return {
    needed: false,
    logData: {
      msg: 'Legacy Visibility Setting Conditions Migration: Skipping component instance',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {},
  };
}

function migrateBindAtPath(
  jsonNode: JSONNode,
  path: ExpressionPath,
  migration: (expr: Expression) => Expression
) {
  if (jsonNode.bind) {
    const expr = Expressions.getIn(jsonNode.bind, path);
    if (expr) {
      jsonNode.bind = Expressions.setIn(jsonNode.bind, path, migration(expr));
    }
  }
}

const constantNull = constant(null);

const isContextRef = matchExpression({
  Variable: Variable.equals('context'),
  _: constantFalse,
});

const isIsUserLoggedInRef = matchExpression({
  Select: ({from, prop}) => {
    return isContextRef(from) && prop === 'isUserLoggedIn';
  },
  _: constantFalse,
});

const getUserLoggedInOrOutCondition = matchExpression({
  Call: ({fun, arg}) => {
    if (isIsUserLoggedInRef(fun)) {
      return Expressions.getEnum(arg) === 'eq' ? 'in' : 'out';
    }
    return null;
  },
  _: constantNull,
});

const isDynamoBooleanToBooleanRef = matchExpression({
  Variable: Variable.equals(['DynamoGateway', 'dynamoBooleanToBoolean']),
  _: constantFalse,
});

/**
 * Migrates a lone visibility condition expression to a direct binding with
 * VisibilityConditions return type that can be attached to replace the
 * visibility conditions array that contained legacy conditions.
 */
const migrateVisibilityConditionToBinding = matchExpression({
  Call: ({fun, arg}) => {
    if (isDynamoBooleanToBooleanRef(fun)) {
      // replace a DynamoGateway::dynamoBooleanToBoolean call with a
      // DynamoGateway::dynamoBooleanToVisibilityConditions call
      return ECall({
        fun: EVariable([
          'DynamoGateway',
          'dynamoBooleanToVisibilityConditions',
        ]),
        arg,
      });
    }

    // default to Visible
    return EList([EBoolean(true)]);
  },
  Select: (_sel, expr) => {
    // the select resolves to a built-in Boolean value, so we wrap it into a
    // the equivalent conditional
    return ECall({
      fun: EVariable(['Visibility', 'booleanToVisibilityConditions']),
      arg: ECall({
        fun: EVariable(['Conditionals', 'Conditional']),
        arg: ERecord({
          rules: EList([
            ERecord({
              condition: ECall({
                fun: EVariable(['Boolean', 'Every']),
                arg: EList([
                  buildNewCondition(
                    ECall({
                      fun: EVariable([
                        'Localization',
                        'LocalesListItemActiveCondition',
                      ]),
                      arg: expr,
                    }),
                    ECall({
                      fun: EVariable(['Localization', 'ActiveEquals']),
                      arg: EUnion({
                        tag: 'True',
                        value: ERecord({}),
                      }),
                    })
                  ),
                ]),
              }),
              output: EUnion({
                tag: 'True',
                value: ERecord({}),
              }),
            }),
          ]),
          fallback: EUnion({
            tag: 'False',
            value: ERecord({}),
          }),
        }),
      }),
    });
  },
  Variable: (_var, expr) => {
    // the variable resolves to a built-in Boolean value, so we wrap it into a
    // the equivalent conditional
    return ECall({
      fun: EVariable(['Visibility', 'booleanToVisibilityConditions']),
      arg: ECall({
        fun: EVariable(['Conditionals', 'Conditional']),
        arg: ERecord({
          rules: EList([
            ERecord({
              condition: ECall({
                fun: EVariable(['Boolean', 'Every']),
                arg: EList([
                  buildNewCondition(
                    ECall({
                      fun: EVariable([
                        'Localization',
                        'LocalesListItemActiveCondition',
                      ]),
                      arg: expr,
                    }),
                    ECall({
                      fun: EVariable(['Localization', 'ActiveEquals']),
                      arg: EUnion({
                        tag: 'True',
                        value: ERecord({}),
                      }),
                    })
                  ),
                ]),
              }),
              output: EUnion({
                tag: 'True',
                value: ERecord({}),
              }),
            }),
          ]),
          fallback: EUnion({
            tag: 'False',
            value: ERecord({}),
          }),
        }),
      }),
    });
  },
  _: () => EList([EBoolean(true)]),
});

/**
 * Migrates a lone visibility condition expression to the equivalent call to
 * Conditions::["Apply predicate"] that can be used to build a conditional that
 * recreates the same visibility conditions configuration.
 */
const migrateVisibilityConditionToPredicate = matchExpression({
  Call: ({fun, arg}) => {
    if (isDynamoBooleanToBooleanRef(fun)) {
      // replace a DynamoGateway::dynamoBooleanToBoolean call with the
      // equivalent conditional condition
      return buildNewCondition(
        ECall({
          fun: EVariable(['DynamoGateway', 'dynamoBooleanOperand']),
          arg,
        }),
        ECall({
          fun: EVariable(['DynamoGateway', 'dynamoBooleanEquals']),
          arg: EUnion({
            tag: 'True',
            value: ERecord({}),
          }),
        })
      );
    }

    return null;
  },
  Select: (_sel, expr) => {
    // the select resolves to a built-in Boolean value, we can simply
    // use it as the operand to a built-in Boolean predicate
    return buildNewCondition(
      ECall({
        fun: EVariable(['Localization', 'LocalesListItemActiveCondition']),
        arg: expr,
      }),
      ECall({
        fun: EVariable(['Localization', 'ActiveEquals']),
        arg: EUnion({
          tag: 'True',
          value: ERecord({}),
        }),
      })
    );
  },
  Variable: (_var, expr) => {
    // the variable resolves to a built-in Boolean value, we can simply
    // use it as the operand to a built-in Boolean predicate
    return buildNewCondition(
      ECall({
        fun: EVariable(['Localization', 'LocalesListItemActiveCondition']),
        arg: expr,
      }),
      ECall({
        fun: EVariable(['Localization', 'ActiveEquals']),
        arg: EUnion({
          tag: 'True',
          value: ERecord({}),
        }),
      })
    );
  },
  // non-binding values are handled elsewhere
  _: constantNull,
});

const migrateVisibilityConditionsExpression = matchExpression({
  List: (list) => {
    const bindingsToMigrate: Expression[] = [];

    for (const item of list) {
      const bool = Expressions.getBoolean(item);

      // if we encounter a false literal, then the visibility is essentially
      // hard-coded to false, so we can stop processing and return a False
      if (typeof bool === 'boolean' && bool === false) {
        return EList([EBoolean(false)]);
      }

      if (Expressions.isBinding(item)) {
        // drop user logged in/out conditions since user systems has been sunset
        // and a security incident forced the login functionality to be disabled
        const userLoggedInOrOutCondition = getUserLoggedInOrOutCondition(item);
        if (userLoggedInOrOutCondition === 'in') {
          // the user is never logged in, so we can treat "user is logged in"
          // conditions as Hidden literals
          return EList([EBoolean(false)]);
        } else if (userLoggedInOrOutCondition === null) {
          // collect all other bindings found from visibility conditions array
          bindingsToMigrate.push(item);
        }
      }
    }

    if (bindingsToMigrate.length === 1 && bindingsToMigrate[0]) {
      // convert single condition to a direct binding on the setting
      return migrateVisibilityConditionToBinding(bindingsToMigrate[0]);
    } else if (bindingsToMigrate.length > 1) {
      const predicates: Expression[] = [];

      for (const binding of bindingsToMigrate) {
        const predicate = migrateVisibilityConditionToPredicate(binding);
        if (predicate) {
          predicates.push(predicate);
        }
      }

      // convert multiple conditions to a conditional on the setting
      if (predicates.length > 0) {
        return ECall({
          fun: EVariable(['Visibility', 'booleanToVisibilityConditions']),
          arg: ECall({
            fun: EVariable(['Conditionals', 'Conditional']),
            arg: ERecord({
              rules: EList([
                ERecord({
                  condition: ECall({
                    fun: EVariable(['Boolean', 'Every']),
                    arg: EList(predicates),
                  }),
                  output: EUnion({
                    tag: 'True',
                    value: ERecord({}),
                  }),
                }),
              ]),
              fallback: EUnion({
                tag: 'False',
                value: ERecord({}),
              }),
            }),
          }),
        });
      }
    }

    return EList([EBoolean(true)]);
  },
  _: identity,
});
